import {
  LETTER_TAG_CATALOG,
  getHypotheticalEmployee,
  getLetterTagValueMap,
  replaceLetterTags,
} from "./letterTagEngine";
import { DEFAULT_LETTER_TEMPLATES } from "./defaultLetterTemplates";

const escapeHtml = (text = "") =>
  String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/**
 * Convert template text (after tag merge) into readable letter HTML.
 * Preserves already-injected HTML blocks (e.g. salary table).
 */
export function formatMergedLetterToHtml(mergedText = "") {
  const raw = String(mergedText || "").replace(/\r\n/g, "\n").trim();
  if (!raw) return "";

  // Protect HTML tables / blocks from escaping
  const blocks = [];
  const protectedText = raw.replace(/<table[\s\S]*?<\/table>|<div[\s\S]*?<\/div>/gi, (m) => {
    const key = `@@HTMLBLOCK${blocks.length}@@`;
    blocks.push(m);
    return key;
  });

  const paragraphs = protectedText.split(/\n{2,}/);
  const htmlParts = paragraphs.map((para) => {
    const lines = para.split("\n").map((l) => l.trimEnd());
    const nonEmpty = lines.filter((l) => l.trim().length);
    if (!nonEmpty.length) return "";

    const isBulletBlock = nonEmpty.every((l) => /^[-•*]\s+/.test(l.trim()));
    if (isBulletBlock) {
      const items = nonEmpty
        .map((l) => l.trim().replace(/^[-•*]\s+/, ""))
        .map((l) => {
          if (/^@@HTMLBLOCK\d+@@$/.test(l)) return `<li>${l}</li>`;
          return `<li>${escapeHtml(l)}</li>`;
        })
        .join("");
      return `<ul style="margin:10px 0 14px 18px;padding:0;">${items}</ul>`;
    }

    // Definition-style rows "Label: value"
    const isDetailBlock =
      nonEmpty.length >= 2 &&
      nonEmpty.every((l) => /^@@HTMLBLOCK\d+@@$/.test(l.trim()) || /^[^:]{2,40}:\s+/.test(l.trim()));
    if (isDetailBlock) {
      const rows = nonEmpty
        .map((l) => {
          const t = l.trim();
          if (/^@@HTMLBLOCK\d+@@$/.test(t)) return t;
          const idx = t.indexOf(":");
          const label = escapeHtml(t.slice(0, idx).trim());
          const value = escapeHtml(t.slice(idx + 1).trim());
          return `<tr>
            <td style="padding:5px 10px 5px 0;vertical-align:top;width:38%;color:#4b5563;font-weight:600;">${label}</td>
            <td style="padding:5px 0;vertical-align:top;color:#111827;">${value}</td>
          </tr>`;
        })
        .join("");
      return `<table style="width:100%;border-collapse:collapse;margin:8px 0 16px;">${rows}</table>`;
    }

    // Lone HTML block (e.g. salary table) — do not wrap in <p>
    if (nonEmpty.length === 1 && /^@@HTMLBLOCK\d+@@$/.test(nonEmpty[0].trim())) {
      return `<div style="margin:12px 0 16px;">${nonEmpty[0].trim()}</div>`;
    }

    const joined = nonEmpty
      .map((l) => {
        const t = l.trim();
        if (/^@@HTMLBLOCK\d+@@$/.test(t)) return t;
        return escapeHtml(t);
      })
      .join("<br/>");
    return `<p style="margin:0 0 12px;text-align:justify;line-height:1.55;">${joined}</p>`;
  });

  let html = htmlParts.filter(Boolean).join("\n");
  blocks.forEach((block, i) => {
    html = html.split(`@@HTMLBLOCK${i}@@`).join(block);
  });
  return html;
}

export function mergeLetterTemplate(content, employee, { forWord = false } = {}) {
  const emp = { ...employee };
  if (forWord) {
    emp.salary_structure_table =
      emp.salary_structure_text ||
      (emp.salary_structure_table && !String(emp.salary_structure_table).includes("<table")
        ? emp.salary_structure_table
        : "");
  } else if (!emp.salary_structure_table) {
    // Only render table HTML when real rows were provided on the merge context
    emp.salary_structure_table = "";
  }
  return replaceLetterTags(content || "", emp, {
    preferPlainSalaryTable: !!forWord,
  });
}

export function buildLetterDocumentHtml({
  title,
  companyName,
  companyAddress,
  bodyHtml,
  employeeName,
  metaRight,
  showConfidential = true,
}) {
  const today =
    metaRight ||
    new Date().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title || "Letter")}</title>
  <style>
    @page { size: A4; margin: 18mm 16mm 18mm 16mm; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      font-family: "Times New Roman", Times, "Segoe UI", Georgia, serif;
      font-size: 11.5pt;
      color: #111827;
      background: #e5e7eb;
    }
    .sheet {
      width: 210mm;
      min-height: 297mm;
      margin: 12px auto;
      background: #fff;
      padding: 22mm 20mm 20mm;
      box-shadow: 0 1px 8px rgba(0,0,0,.12);
    }
    .letterhead {
      border-bottom: 2.5px solid #1f2937;
      padding-bottom: 12px;
      margin-bottom: 18px;
    }
    .company-name {
      font-size: 18pt;
      font-weight: 700;
      letter-spacing: .02em;
      color: #111827;
      margin: 0 0 4px;
      font-family: "Segoe UI", Arial, sans-serif;
    }
    .company-address {
      font-size: 9.5pt;
      color: #4b5563;
      line-height: 1.4;
      font-family: "Segoe UI", Arial, sans-serif;
    }
    .meta-row {
      display: flex;
      justify-content: space-between;
      gap: 16px;
      margin: 14px 0 18px;
      font-size: 10.5pt;
      font-family: "Segoe UI", Arial, sans-serif;
    }
    .doc-title {
      text-align: center;
      font-size: 13.5pt;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: .06em;
      margin: 8px 0 20px;
      text-decoration: underline;
      font-family: "Segoe UI", Arial, sans-serif;
    }
    .body { line-height: 1.55; }
    .body table { font-family: "Segoe UI", Arial, sans-serif; font-size: 10.5pt; }
    .sign-block { margin-top: 36px; }
    .sign-line { margin-top: 42px; border-top: 1px solid #9ca3af; width: 220px; padding-top: 6px; }
    .footer-note {
      margin-top: 28px;
      padding-top: 10px;
      border-top: 1px solid #e5e7eb;
      font-size: 8.5pt;
      color: #6b7280;
      font-family: "Segoe UI", Arial, sans-serif;
    }
    .appendix { page-break-before: always; }
    .appendix h2 {
      font-family: "Segoe UI", Arial, sans-serif;
      font-size: 13pt;
      margin: 0 0 8px;
    }
    .appendix p {
      font-family: "Segoe UI", Arial, sans-serif;
      font-size: 10pt;
      color: #4b5563;
      margin: 0 0 14px;
    }
    .tag-table {
      width: 100%;
      border-collapse: collapse;
      font-family: "Segoe UI", Arial, sans-serif;
      font-size: 9.5pt;
    }
    .tag-table th, .tag-table td {
      border: 1px solid #d1d5db;
      padding: 7px 8px;
      vertical-align: top;
      text-align: left;
    }
    .tag-table th { background: #f3f4f6; }
    .tag-table code {
      font-family: Consolas, "Courier New", monospace;
      font-size: 9pt;
      background: #f9fafb;
      padding: 1px 4px;
      border-radius: 3px;
    }
    @media print {
      body { background: #fff; }
      .sheet { margin: 0; box-shadow: none; width: auto; min-height: auto; }
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="letterhead">
      <p class="company-name">${escapeHtml(companyName || "Company Name")}</p>
      <div class="company-address">${escapeHtml(companyAddress || "")}</div>
    </div>
    <div class="meta-row">
      <div>${employeeName ? `Ref: HR/LET/${escapeHtml(employeeName).slice(0, 24)}` : "Ref: HR/LET/SAMPLE"}</div>
      <div>Date: ${escapeHtml(today)}</div>
    </div>
    ${title ? `<div class="doc-title">${escapeHtml(title)}</div>` : ""}
    <div class="body">${bodyHtml || ""}</div>
    ${
      showConfidential
        ? `<div class="footer-note">This is a system-generated sample / official communication of ${escapeHtml(
            companyName || "the company"
          )}. Please retain for your records.</div>`
        : ""
    }
  </div>
</body>
</html>`;
}

export function buildTagGuideAppendixHtml({
  usedTags = [],
  employee,
  catalog = LETTER_TAG_CATALOG,
  showSampleValues = false,
}) {
  const allDefs = Object.values(catalog).flat();
  const valueMap = showSampleValues
    ? getLetterTagValueMap(employee || {}, { preferPlainSalaryTable: true })
    : {};
  const rows = usedTags
    .map((tag) => {
      const def = allDefs.find((x) => x.tag === tag);
      const sample = showSampleValues ? valueMap[tag] ?? "" : "";
      const samplePreview =
        String(sample).length > 80 ? `${String(sample).slice(0, 77)}…` : String(sample);
      return `<tr>
        <td><code>${escapeHtml(tag)}</code></td>
        <td>${escapeHtml(def?.description || "Dynamic merge field")}</td>
        <td>${
          showSampleValues
            ? escapeHtml(samplePreview || "(empty until employee data is selected)")
            : "Filled at Generate from employee / company / salary structure"
        }</td>
      </tr>`;
    })
    .join("");

  return `
  <div class="sheet appendix">
    <h2>Tag mapping guide</h2>
    <p>
      Copy these placeholders into your Word (.docx) or text template exactly as shown.
      Preview / Generate replaces each tag with live employee, company, or salary-structure values.
    </p>
    <table class="tag-table">
      <thead>
        <tr>
          <th style="width:28%;">Tag</th>
          <th style="width:37%;">Meaning</th>
          <th>${showSampleValues ? "Sample value" : "Source when generated"}</th>
        </tr>
      </thead>
      <tbody>
        ${
          rows ||
          `<tr><td colspan="3">No tags found in this sample template.</td></tr>`
        }
      </tbody>
    </table>
    <p style="margin-top:16px;">
      Tip: insert <code>{{salary_structure_table}}</code> where you want the employee CTC breakdown.
      Amounts come only from that employee’s assigned salary structure — never from invented defaults.
    </p>
  </div>`;
}

export function extractTagsFromText(text = "") {
  const matches = String(text).match(/\{\{\s*[a-zA-Z0-9_]+\s*\}\}/g) || [];
  // normalize spacing inside braces
  return Array.from(
    new Set(
      matches.map((t) => {
        const name = t.replace(/[{}]/g, "").trim();
        return `{{${name}}}`;
      })
    )
  );
}

export function getSystemSampleTemplate(letterType) {
  const list = DEFAULT_LETTER_TEMPLATES?.[letterType];
  if (!Array.isArray(list) || !list.length) return null;
  return list.find((t) => String(t.id).endsWith("-1")) || list[0];
}

/**
 * Sample format for authors: keeps {{tags}} visible (default),
 * plus a tag-mapping appendix. Use mode: 'filled' only when generating with real data.
 */
export function buildSampleFormatDocument({
  letterType,
  typeLabel,
  company,
  template,
  mode = "tags",
  employee: employeeOverride,
}) {
  const sample = template || getSystemSampleTemplate(letterType);
  if (!sample) return null;

  const keepTags = mode !== "filled";
  const employee =
    employeeOverride ||
    getHypotheticalEmployee(company || {}, { salaryRows: [] });
  const usedTags = extractTagsFromText(
    `${sample.header || ""}\n${sample.content || ""}\n${sample.footer || ""}`
  );

  const { bodyHtml } = keepTags
    ? (() => {
        const rawBody = [sample.header, sample.content, sample.footer]
          .filter(Boolean)
          .join("\n\n");
        // Keep tags literally; highlight placeholders in mono
        let html = formatMergedLetterToHtml(rawBody);
        html = html.replace(
          /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g,
          '<code style="font-family:Consolas,monospace;background:#f3f4f6;padding:1px 4px;border-radius:3px;">{{$1}}</code>'
        );
        return { bodyHtml: html };
      })()
    : buildResolvedLetterParts(sample, employee, { forWord: false });

  const letterHtml = buildLetterDocumentHtml({
    title: `${typeLabel || "Official Letter"}${keepTags ? " — Sample format (tags)" : ""}`,
    companyName: keepTags
      ? "{{company_name}}"
      : employee.company_name || company?.name,
    companyAddress: keepTags
      ? "{{company_address}}"
      : employee.company_address || company?.address,
    bodyHtml,
    employeeName: keepTags ? "SAMPLE" : employee.employeeId,
    showConfidential: true,
  });

  // In tags mode, letterhead also shows literal company tags (already set above as text).
  // Re-escape if company tags were inserted as raw into title attributes — they're plain text in template literals.

  const appendix = buildTagGuideAppendixHtml({
    usedTags,
    employee,
    showSampleValues: !keepTags,
  });
  return letterHtml.replace("</body>", `${appendix}\n</body>`);
}

export function openPrintableHtml(html, { autoPrint = true } = {}) {
  const w = window.open("", "_blank");
  if (!w) {
    alert("Please allow pop-ups to preview / print the letter.");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  if (autoPrint) {
    w.onload = () => {
      try {
        w.focus();
        w.print();
      } catch {
        /* ignore */
      }
    };
  }
}

export function buildPreviewShellHtml(bodyHtml, { companyName, companyAddress, title } = {}) {
  return `
    <div style="max-width:820px;margin:0 auto;background:#fff;padding:28px 32px;border:1px solid #e5e7eb;border-radius:8px;">
      <div style="border-bottom:2px solid #1f2937;padding-bottom:10px;margin-bottom:16px;">
        <div style="font-size:18px;font-weight:700;font-family:Segoe UI,Arial,sans-serif;">${escapeHtml(
          companyName || "Company"
        )}</div>
        <div style="font-size:12px;color:#4b5563;font-family:Segoe UI,Arial,sans-serif;">${escapeHtml(
          companyAddress || ""
        )}</div>
      </div>
      ${
        title
          ? `<div style="text-align:center;font-weight:700;text-transform:uppercase;letter-spacing:.04em;margin:0 0 18px;font-family:Segoe UI,Arial,sans-serif;font-size:14px;text-decoration:underline;">${escapeHtml(
              title
            )}</div>`
          : ""
      }
      <div style="font-family:'Times New Roman',Times,serif;font-size:12.5px;line-height:1.55;color:#111827;">
        ${bodyHtml}
      </div>
    </div>
  `;
}

/**
 * Resolve template header/content/footer into formatted letter body HTML (with salary table).
 */
export function buildResolvedLetterParts(template, employee, { forWord = false } = {}) {
  const emp = { ...employee };
  if (forWord) {
    emp.salary_structure_table =
      emp.salary_structure_text ||
      (emp.salary_structure_table && !String(emp.salary_structure_table).includes("<table")
        ? emp.salary_structure_table
        : "");
  }
  const mergedHeader = template?.header ? mergeLetterTemplate(template.header, emp, { forWord }) : "";
  const mergedContent = mergeLetterTemplate(template?.content || "", emp, { forWord });
  const mergedFooter = template?.footer ? mergeLetterTemplate(template.footer, emp, { forWord }) : "";

  let bodyHtml = formatMergedLetterToHtml(mergedContent);
  if (mergedHeader) {
    bodyHtml =
      `<div style="margin-bottom:14px;">${formatMergedLetterToHtml(mergedHeader)}</div>` + bodyHtml;
  }
  if (mergedFooter) {
    bodyHtml += `<div class="sign-block" style="margin-top:28px;">${formatMergedLetterToHtml(
      mergedFooter
    )}</div>`;
  }
  return { mergedHeader, mergedContent, mergedFooter, bodyHtml };
}
